import * as React from "react";
import { Link, useParams, useHistory } from "react-router-dom";
import { WebPortalClient, EcgAnalysisSetTest, SessionNote, TestNote } from "webportalclient";
import { SessionMetadata, OverreadStatusIdEnum, AnalysisSet } from "../misc/api/apiTypes";
import LoadingWithMessage from "../misc/components/LoadingWithMessage";
import { DateTimeAccuracy, LocalizedDateTime } from "../misc/components/LocalizedDateTime";
import { LeanSessionMetadata, LeanSessionMetadataTable } from "../PulmonaryFunctionTests/Shared/SessionMetadataTable";
import { ApiApplications } from "../misc/api/DeviceLinkApi/Applications";
import * as CalEcgApi from "../misc/api/DeviceLinkApi/CalEcg";
import { EcgTestAnnotations } from "./EcgTestAnnotations";
import DocumentTitle from "../misc/components/DocumentTitle";
import { SessionTypes } from "../misc/api/ApiSessionTypes";
import { EcgDashboard } from "./EcgDashboard";
import { PageValidatedAction, ToggleAlertFixedCommentCheckedAction, ToggleTestFixedCommentCheckedAction, useFixedComments } from "./UseFixedComments";
import { EcgCommentModals } from "./EcgCommentModals";
import { NotesModal } from "../misc/components/NotesModal";
import * as Moment from 'moment';
import { useSecurityContext } from "../Core/SecurityContext";
import AsyncActionButton from "../misc/components/AsyncActionButton";
import Alert, { AlertCategoryEnum } from "../misc/components/Alert";
import { useReferenceContextProvider } from "../Core/ReferenceContextProvider";
import { useCurrentStudyConfigurationContext } from "../Core/CurrentStudyConfigurationContext";
import { EcgAnalysisSetTypeEnum } from "../misc/api/ApiEcgAnalysisSetType";
import { CalculateHeartRate, CalculateQTcBazett, CalculateQTcFramingham, CalculateQTcFridericia, CalculateQTcHodges, DeriveParametersFromAnnotations } from "./EcgCalculations";
import { ReviewAnnotationDialog } from "./ReviewAnnotationDialog";
import { EcgResultsPanel } from "./EcgResultsPanel";
import { PermissionEnum } from "../misc/api/ApiPermissionEnum";
import { useWebPortalApiClient } from "../misc/api/WebPortalTokenClient";

export interface TestLeadAnnotations {
    ampsAnnotationsText: string;
    annotations: CalEcgApi.LeadAnnotation[];
}

interface AnalysisButtons {
    analysis: boolean;
    analysisLoading: boolean;
    getAnnotations: boolean;
    close: boolean;
}

export const EcgAnalysisDetail = () => {
    const { analysisSetTestId } = useParams<{ analysisSetTestId: string }>();
    const { analyseMode } = useParams<{ analyseMode: string }>();
    const history = useHistory();
    const client = useWebPortalApiClient();
    const { currentUser, currentUserPermissions } = useSecurityContext();
    const { overreadStatuses } = useReferenceContextProvider();
    const { study } = useCurrentStudyConfigurationContext();
    const [sessionMetadata, setSessionMetadata] = React.useState<LeanSessionMetadata>();
    const [analysisSetTest, setAnalysisSetTest] = React.useState<EcgAnalysisSetTest>();
    const [testAnnotations, setTestAnnotations] = React.useState<TestLeadAnnotations>();
    const [analysisButtons, setAnalysisButtons] = React.useState<AnalysisButtons>();
    const [error, setError] = React.useState<string>();

    const [notes, setNotes] = React.useState<TestNote[]>([]);
    const [notesVisible, setNotesVisible] = React.useState(false);
    const [isAddingNewNote, setIsAddingNewNote] = React.useState(false);
    const [isSubmittingAccept, setIsSubmittingAccept] = React.useState(false);
    const [isSubmittingReject, setIsSubmittingReject] = React.useState(false);
    const [canEdit, setCanEdit] = React.useState(false);
    const [reviewAnnotationVisible, setReviewAnnotationVisible] = React.useState(false);

    const setDefaultButtons = (): AnalysisButtons => {
        return {
            analysis: true,
            analysisLoading: false,
            getAnnotations: false,
            close: false
        };
    }

    React.useEffect(() => {
        setCanEdit(analyseMode === "true");
    }, [analyseMode])

    React.useEffect(() => {
        const getData = async () => {
            // HACK: Get Single working correctly at API level
            const ecgAnalysisSetTestResult = await (await client.client.getEcgAnalysisSetTests(undefined, `id eq ${analysisSetTestId}`, undefined, "test,analysisset")).value[0];

            if (ecgAnalysisSetTestResult.analyst === null) {
                await client.client.patchEcgAnalysisSet({
                    status: OverreadStatusIdEnum.Pending,
                    started: Moment().toISOString(),
                }, ecgAnalysisSetTestResult.analysisSetId, "");

                await client.client.patchEcgAnalysisSetTest({
                    analystId: currentUser.Id,
                }, ecgAnalysisSetTestResult.id, "");
            }

            if (analyseMode === "true" && (ecgAnalysisSetTestResult.analysisSet.type === EcgAnalysisSetTypeEnum.Overread || ecgAnalysisSetTestResult.analysisSet.type === EcgAnalysisSetTypeEnum.QC)) {
                setCanEdit(true);
            }

            if (ecgAnalysisSetTestResult.analyst !== null && ecgAnalysisSetTestResult.analyst !== currentUser.Email) {
                setCanEdit(false);
            }

            setAnalysisSetTest(ecgAnalysisSetTestResult);
            setTestAnnotations({
                ampsAnnotationsText: ecgAnalysisSetTestResult.ampsAnnotationsTextBase64,
                annotations: ecgAnalysisSetTestResult.annotations.map(a => ({
                    annotation: a.annotation,
                    leadIndex: a.leadIndex,
                    beatIndex: a.beatIndex,
                    value: a.value
                }))
            });
            const specificSession = await client.client.getEcgSession(ecgAnalysisSetTestResult.analysisSet.sessionId, undefined, "visit($expand=subject($expand=site))");

            setAnalysisButtons(setDefaultButtons());

            const statusName = overreadStatuses.find(os => os.id === ecgAnalysisSetTestResult.analysisSet.status).name;

            setSessionMetadata({
                Id: specificSession.id,
                SiteId: specificSession.visit.subject.site.id,
                SiteName: specificSession.visit.subject.site.name,
                SiteCountryName: specificSession.visit.subject.site.country,
                SiteNumber: specificSession.visit.subject.site.number,
                Technician: specificSession.technician,
                SubjectId: specificSession.visit.subject.id,
                SubjectNumber: specificSession.visit.subject.number,
                VisitName: specificSession.visit.name,
                SessionName: specificSession.name,
                SessionDateTime: specificSession.occurred,
                SessionTypeId: specificSession.sessionType,
                ReceivedDateTime: specificSession.created,
                SessionTypeName: specificSession.sessionType,
                Status: statusName,
                StatusId: ecgAnalysisSetTestResult.analysisSet.status,
                Overreader: ecgAnalysisSetTestResult.analyst,
                AnalysisSets: [ecgAnalysisSetTestResult.analysisSet].map(a => ({
                    Id: a.id,
                    Analyst: {
                        Username: ecgAnalysisSetTestResult.analyst
                    },
                    Status: {
                        Name: statusName
                    }
                }))
            });

            for (let i = 0; i < ecgAnalysisSetTestResult.intepretations.length; i++) {
                fixedCommentsDispatch(ToggleTestFixedCommentCheckedAction.create(ecgAnalysisSetTestResult.testId, ecgAnalysisSetTestResult.intepretations[i].fixedCommentId, true));
            }

            for (let i = 0; i < ecgAnalysisSetTestResult.alerts.length; i++) {
                fixedCommentsDispatch(ToggleAlertFixedCommentCheckedAction.create(ecgAnalysisSetTestResult.testId, ecgAnalysisSetTestResult.alerts[i].fixedCommentId, true));
            }

            await fetchTestNotes(ecgAnalysisSetTestResult);
        }

        getData();
    }, [analysisSetTestId]);

    React.useEffect(() => {
        document.getElementsByClassName('alert-danger')[0]?.scrollIntoView({ behavior: 'smooth' });
    }, [error])

    const onAnalyse = async (testId: string) => {
        setAnalysisButtons({
            ...analysisButtons,
            analysisLoading: true,
        });
        setError(undefined);

        try {
            const apps = await ApiApplications();
            const calECGApp = apps.find(x => x.name === "CalECG");

            const annotationEcgWrapper = await client.client.annotatedEcg({
                testId: testId,
            });
            const ecgOpenResult = await CalEcgApi.OpenEcg(calECGApp.id, annotationEcgWrapper.text);

            if (ecgOpenResult !== CalEcgApi.EcgFileOpenResult.RhythmOnly && ecgOpenResult !== CalEcgApi.EcgFileOpenResult.RepresentativeBeat) {
                // TODO: Error handling
                return;
            }

            if (canEdit) {
                await CalEcgApi.SetPermission(calECGApp.id, CalEcgApi.CalEcgPermissions.All, true);
            } else {
                await CalEcgApi.SetPermission(calECGApp.id, CalEcgApi.CalEcgPermissions.All, false);
                await CalEcgApi.SetPermission(calECGApp.id, CalEcgApi.CalEcgPermissions.ViewDemographics, true);
            }

            await CalEcgApi.SetTitle(calECGApp.id, `${sessionMetadata.SubjectNumber} - ${sessionMetadata.SessionName} - Recording ${analysisSetTest.test.index}`);
            await CalEcgApi.Protocol(calECGApp.id);

            await CalEcgApi.SetDigitalDefaults(calECGApp.id);

            if (testAnnotations.ampsAnnotationsText) {
                const importResult = await CalEcgApi.ImportAnnotationsTextFile(calECGApp.id, testAnnotations.ampsAnnotationsText);
            }

            setAnalysisButtons({
                analysis: false,
                analysisLoading: false,
                getAnnotations: true,
                close: true,
            });

            await CalEcgApi.ShowWindow(calECGApp.id, true);
        } catch {
            setError("Failed to launch CalECG please ensure CalECG and Vitalograph Device Link are installed.");
            setAnalysisButtons(setDefaultButtons());
        }
    }

    const onGetAnnotations = async () => {
        const apps = await ApiApplications();
        const calECGApp = apps.find(x => x.name === "CalECG");

        const result = await CalEcgApi.GetAnnotations(calECGApp.id);
        const annotationFileResult = await CalEcgApi.ExportEcgToBase64(calECGApp.id, 3);

        setTestAnnotations({
            ampsAnnotationsText: annotationFileResult,
            annotations: result,
        });
    }

    const onCloseCalEcg = async () => {
        const apps = await ApiApplications();
        const calECGApp = apps.find(x => x.name === "CalECG");

        if (calECGApp.isLaunched)
            await CalEcgApi.CloseApplication(calECGApp.id);

        setAnalysisButtons(setDefaultButtons());
    }

    const onComplete = async (analysisSetTestId: string, testId: string) => {
        setError(undefined);
        setIsSubmittingAccept(true);

        const interpretations = fixedCommentsState.testFixedComments.find(x => x.id === testId);
        const commentsLength = interpretations?.comments.filter(c => c.selected).length;

        if (commentsLength === undefined || commentsLength === 0) {
            setError("At least one interpretation must be selected.");
            setIsSubmittingAccept(false);
            return;
        }

        const { rr, qt } = DeriveParametersFromAnnotations(testAnnotations.annotations);

        const heartRate = CalculateHeartRate(rr);

        await client.client.patchEcgAnalysisSetTest({
            accepted: true,
            analysisSetId: analysisSetTest.analysisSetId,
            testId: testId,
            ampsAnnotationsTextBase64: testAnnotations.ampsAnnotationsText,
            rr: rr,
            hr: heartRate,
            qTcBaz: CalculateQTcBazett(qt, rr),
            qTcFri: CalculateQTcFridericia(qt, rr),
            qTcFra: CalculateQTcFramingham(qt, rr),
            qTcHod: CalculateQTcHodges(qt, heartRate),
            analysisCompleted: Moment().toISOString(true),
            annotations: testAnnotations.annotations.map(a => ({
                annotation: a.annotation,
                beatIndex: a.beatIndex,
                leadIndex: a.leadIndex,
                value: a.value
            })),
            intepretations: interpretations.comments.filter(c => c.selected).map(c => ({
                analysisSetTestId: analysisSetTestId,
                fixedCommentId: c.id,
            })),
            alerts: fixedCommentsState.alertFixedComments.find(x => x.id === testId).comments.filter(c => c.selected).map(c => ({
                analysisSetTestId: analysisSetTestId,
                fixedCommentId: c.id,
            }))
        }, analysisSetTestId, "");

        const allTests = await client.client.getEcgAnalysisSetTests(undefined, `analysisSetId eq ${analysisSetTest.analysisSetId}`, undefined);
        if (allTests.value.every(x => x.accepted)) {
            await client.client.patchEcgAnalysisSet({
                status: OverreadStatusIdEnum.Accepted,
            }, analysisSetTest.analysisSetId, "");
        }

        await onCloseCalEcg();

        setIsSubmittingAccept(false);
        history.push("/ecg/analysis", { refresh: true });
    }

    const onReview = async (analysisSetTestId: string, accepted: boolean) => {
        accepted ? setIsSubmittingAccept(true) : setIsSubmittingReject(true);
        if (!accepted) {
            setReviewAnnotationVisible(true);
            setIsSubmittingAccept(false);
            setIsSubmittingReject(false);
            return;
        }

        await onReviewComplete(analysisSetTestId, accepted);
        setIsSubmittingAccept(false);
        setIsSubmittingReject(false);
    }

    const onReviewComplete = async (analysisSetTestId: string, accepted: boolean, auditAnnotation: string = null) => {
        setError(undefined);
        setReviewAnnotationVisible(false);

        await client.client.patchEcgAnalysisSetTest({
            accepted: accepted,
            analysisCompleted: Moment().toISOString(true),
        }, analysisSetTestId, auditAnnotation);

        const allTests = await client.client.getEcgAnalysisSetTests(undefined, `analysisSetId eq ${analysisSetTest.analysisSetId}`, undefined);
        if (allTests.value.every(x => x.accepted)) {
            await client.client.patchEcgAnalysisSet({
                status: OverreadStatusIdEnum.Accepted,
            }, analysisSetTest.analysisSetId, "");
        }

        setIsSubmittingAccept(false);
        setIsSubmittingReject(false);
        history.push("/ecg/analysis", { refresh: true });
    }

    const fetchTestNotes = async (analysisSetTest: EcgAnalysisSetTest) => {
        if (analysisSetTest.analysisSet.type !== EcgAnalysisSetTypeEnum.QC) {
            const notesResult = await client.client.getTestNotes(undefined, `testId eq ${analysisSetTest.testId}`);
            const sessionNotesResult = await client.client.getSessionNotes(undefined, `sessionId eq ${analysisSetTest.analysisSet.sessionId}`);
            setNotes([
                ...notesResult.value,
                ...sessionNotesResult.value
            ].sort((a, b) => Moment(a.recorded).diff(Moment(b.recorded))));
        } else {
            setNotes([]);
        }
    }

    const addNewNote = async (comment: string) => {
        await client.client.postTestNote(
        {
            testId: analysisSetTest.testId,
            userEmailAddress: currentUser.Email,
            comment: comment,
            recorded: Moment().toISOString()
        })
        await fetchTestNotes(analysisSetTest);
    }

    const [fixedCommentsState, fixedCommentsDispatch] = useFixedComments(analysisSetTest ? [analysisSetTest] : [], SessionTypes.Ecg);

    if (!(sessionMetadata && analysisButtons && analysisSetTest && fixedCommentsState.areAvailableFixedCommentsLoaded !== null))
        return <LoadingWithMessage message="Loading ECG Recording..." />;

    const hasAddingNotePermission = currentUserPermissions.some(x => x.Id == PermissionEnum.AddEcgSessionNotes);
    const hasViewNotePermission = currentUserPermissions.some(x => x.Id == PermissionEnum.ViewEcgSessionNotes);

    const actionControls = <>
        <td><b>Analysis</b></td>
        <td>
            <AsyncActionButton disabled={!analysisButtons.analysis} isSubmitting={analysisButtons.analysisLoading} className="btn btn-primary" onClick={() => onAnalyse(analysisSetTest.testId)}>Open CalECG</AsyncActionButton>&nbsp;
            <button disabled={!analysisButtons.getAnnotations || !canEdit} className="btn btn-primary" onClick={() => onGetAnnotations()}>Sync Annotations</button>&nbsp;
            <button disabled={!analysisButtons.close} className="btn btn-primary" onClick={() => onCloseCalEcg()}>Close</button>
        </td>
    </>

    return (
        <React.StrictMode>
            <DocumentTitle title="ECG Analysis | Vitalograph Web Portal">
                <Alert category={AlertCategoryEnum.Danger} show={error !== undefined}>
                    {error}
                </Alert>
                <div className="row">
                    <div className="col-md-6">
                        <h2 className="text-primary">ECG Recording</h2>
                    </div>
                    <div className="h2 col-md-6">
                        <Link to="/ecg/analysis" className="pull-right btn btn-outline btn-primary" onClick={() => onCloseCalEcg()}>Back to List</Link>
                    </div>
                </div>
                <LeanSessionMetadataTable
                    sessionMetadata={sessionMetadata}
                    selectedAnalysisSetId={analysisSetTest.analysisSetId}
                />

                <h2 className="text-primary">Results</h2>              
                <React.Fragment key={`ResultPanel${analysisSetTest.id}`}>
                    <EcgResultsPanel test={analysisSetTest.test} controls={actionControls}>
                        <EcgTestAnnotations testAnnotations={testAnnotations?.annotations} />
                    </EcgResultsPanel>
                    
                    <EcgDashboard
                        test={analysisSetTest}
                        isAnalysis={analysisSetTest.analysisSet.type === EcgAnalysisSetTypeEnum.Overread || analysisSetTest.analysisSet.type === EcgAnalysisSetTypeEnum.QC}
                        canEdit={canEdit}
                        fixedCommentsDispatch={fixedCommentsDispatch}
                        fixedCommentsState={fixedCommentsState}
                        setNotesVisible={(v) => setNotesVisible(v)}
                        notes={notes}
                        isSubmittingAccept={isSubmittingAccept}
                        isSubmittingReject={isSubmittingReject}
                        canViewNotes={hasViewNotePermission}
                        canAddNotes={hasAddingNotePermission}
                        onComplete={() => onComplete(analysisSetTest.id, analysisSetTest.testId)}
                        onReview={(accepted) => onReview(analysisSetTest.id, accepted)}
                    />
                </React.Fragment>
                    
                <EcgCommentModals
                    editable={canEdit}
                    testFixedComments={fixedCommentsState.testFixedComments}
                    alertFixedComments={fixedCommentsState.alertFixedComments}
                    setIsPageValidated={(isValidated) => fixedCommentsDispatch(PageValidatedAction.create(isValidated))}
                    isPageValid={fixedCommentsState.isPageValidated}
                    dispatch={fixedCommentsDispatch}
                />
                {hasViewNotePermission ? <NotesModal
                    isVisible={notesVisible}
                    header="Recording Notes"
                    isAddingNote={isAddingNewNote}
                    notes={notes}
                    onAdd={annotation => addNewNote(annotation)}
                    onCloseModal={() => setNotesVisible(false)}
                    disabled={!canEdit || analysisSetTest.analysisSet.type === EcgAnalysisSetTypeEnum.QC || !hasAddingNotePermission}
                /> : null}
                {reviewAnnotationVisible ? <ReviewAnnotationDialog
                    onClose={() => setReviewAnnotationVisible(false)}
                    onSubmit={(audit) => onReviewComplete(analysisSetTestId, false, audit.Annotation)}
                /> : null}
                </DocumentTitle>
        </React.StrictMode>
    );
}
